import { BarElement, CategoryScale, Chart, Chart as ChartJS, LinearScale, Plugin, Tooltip, TooltipItem } from 'chart.js'
import { FC, useEffect, useMemo, useRef } from 'react'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { useUAInsightsChartTheme } from '../../helpers/chartTheme'
import { getMotivationChartHeight } from '../../helpers/motivationChartHeight'
import { MOTIVATION_SOURCE_LABEL_KEYS, MOTIVATION_SOURCE_ORDER, useMotivationSourceColors } from '../../helpers/motivationSourceColors'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationScoresBySource, MotivationSource } from '../../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

interface MotivationBarChartProps {
  motivationScores: MotivationScoresBySource
  /** Sources whose dataset should be hidden (driven by the shared legend). */
  hiddenSources?: ReadonlySet<MotivationSource>
  /**
   * Called with the motivation id when the user clicks a bar or its row label. When provided the chart turns the bars/labels into
   * affordances (pointer cursor) that drill into the game's Motivation view.
   */
  onSelectMotivation?: (motivationId: string) => void
}

/**
 * Draws an alternating very-light-grey strip behind every other category row
 * so the eye can quickly tell which group of three bars belongs to which
 * motivation. Drawn before the bars so the bars sit on top.
 */
/**
 * Vertical pixel span (top + height) of a category row, using a uniform
 * step-height centred on the row's tick. Sampling the gap to an adjacent row
 * (rather than the scale's top/bottom edge) keeps the first and last rows the
 * same height as the middle ones, so a row band never overshoots into the
 * scale's padding.
 */
const rowSpan = (chart: Chart, index: number): { top: number; height: number } => {
  const yScale = chart.scales.y
  const labels = chart.data.labels ?? []
  const center = yScale.getPixelForValue(index)
  let step: number
  if (labels.length > 1) {
    const neighbor = index + 1 < labels.length ? index + 1 : index - 1
    step = Math.abs(yScale.getPixelForValue(neighbor) - center)
  } else {
    step = chart.chartArea.bottom - chart.chartArea.top
  }
  return { top: center - step / 2, height: step }
}

const createZebraRowsPlugin = (fillStyle: string): Plugin<'bar'> => ({
  id: 'motivationZebraRows',
  beforeDatasetsDraw(chart: Chart) {
    const yScale = chart.scales.y
    const xScale = chart.scales.x
    if (!yScale || !xScale) return

    const ctx = chart.ctx
    const labels = chart.data.labels ?? []
    const left = xScale.left
    const width = xScale.right - xScale.left

    ctx.save()
    ctx.fillStyle = fillStyle
    labels.forEach((_, index) => {
      if (index % 2 !== 0) return
      const { top, height } = rowSpan(chart, index)
      ctx.fillRect(left, top, width, height)
    })
    ctx.restore()
  },
})

/** Chart instance carrying the currently hovered motivation row (for the highlight plugin). */
type HoverableChart = Chart & { $grHoveredRow?: number | null }

/**
 * Maps a canvas pixel position to the motivation row index for the whole row
 * band — the y-axis label gutter as well as the plot area — so hovering or
 * clicking anywhere on a row (label included) resolves to that motivation,
 * matching the dashboard's Creative Motivations rows. Returns `null` when the
 * pointer is outside the plotted rows.
 */
const resolveRowIndexAt = (chart: Chart, x: number, y: number, count: number): number | null => {
  const yScale = chart.scales.y
  if (!yScale) return null
  if (x < 0 || x > chart.width) return null
  if (y < chart.chartArea.top || y > chart.chartArea.bottom) return null
  const value = yScale.getValueForPixel(y)
  if (value == null) return null
  const index = Math.round(value)
  return index >= 0 && index < count ? index : null
}

/**
 * Paints a subtle full-row wash behind the hovered motivation (label gutter +
 * bars), reproducing the `action.hover` highlight the dashboard's Creative
 * Motivations rows use. Drawn after the zebra stripe but before the bars so the
 * bars stay crisp on top. The hovered index is stashed on the chart instance by
 * the native mousemove listener.
 */
const createRowHighlightPlugin = (fillStyle: string): Plugin<'bar'> => ({
  id: 'motivationRowHighlight',
  beforeDatasetsDraw(chart: Chart) {
    const index = (chart as HoverableChart).$grHoveredRow
    if (index == null || index < 0) return
    const labels = chart.data.labels ?? []
    if (!chart.scales.y || index >= labels.length) return

    const { top, height } = rowSpan(chart, index)
    const ctx = chart.ctx
    // Span from the canvas left edge (covering the row label) to the end of the
    // plot area, with a small vertical inset + rounded corners so it reads like
    // the dashboard's rounded row hover rather than a hard band.
    const x = 0
    const width = chart.chartArea.right
    const insetTop = top + 1
    const insetHeight = Math.max(0, height - 2)

    ctx.save()
    ctx.fillStyle = fillStyle
    const roundable = ctx as CanvasRenderingContext2D & { roundRect?: (x: number, y: number, w: number, h: number, r: number) => void }
    if (typeof roundable.roundRect === 'function') {
      ctx.beginPath()
      roundable.roundRect(x, insetTop, width, insetHeight, 6)
      ctx.fill()
    } else {
      ctx.fillRect(x, insetTop, width, insetHeight)
    }
    ctx.restore()
  },
})

export const MotivationBarChart: FC<MotivationBarChartProps> = ({ motivationScores, hiddenSources, onSelectMotivation }) => {
  const { t } = useTranslation('ua-insights')
  const sourceColors = useMotivationSourceColors()
  const chartTheme = useUAInsightsChartTheme()
  const { motivationLabel } = useUATaxonomy()

  const labels = useMemo(() => motivationScores.creativeAds.map((s) => motivationLabel(s.motivation)), [motivationScores, motivationLabel])
  // Motivation ids, index-aligned with `labels`, so a bar/label click can
  // resolve back to the motivation it represents.
  const motivationIds = useMemo(() => motivationScores.creativeAds.map((s) => s.motivation.id), [motivationScores])

  const chartRef = useRef<ChartJS<'bar'> | null>(null)

  // Hover + click are handled with native canvas listeners rather than Chart.js
  // `onHover`/`onClick`: native events fire across the entire canvas — including
  // the y-axis label gutter left of the plot — so a row label is just as
  // clickable/hoverable as its bars, and `mouseleave` reliably clears the
  // hover highlight when the pointer exits the chart.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return undefined
    const canvas = chart.canvas

    const setHoveredRow = (index: number | null) => {
      const hoverable = chart as HoverableChart
      if ((hoverable.$grHoveredRow ?? null) === (index ?? null)) return
      hoverable.$grHoveredRow = index
      chart.render()
    }

    const handleMove = (event: MouseEvent) => {
      const index = onSelectMotivation ? resolveRowIndexAt(chart, event.offsetX, event.offsetY, motivationIds.length) : null
      canvas.style.cursor = index != null ? 'pointer' : 'default'
      setHoveredRow(index)
    }

    const handleLeave = () => {
      canvas.style.cursor = 'default'
      setHoveredRow(null)
    }

    const handleClick = (event: MouseEvent) => {
      if (!onSelectMotivation) return
      const index = resolveRowIndexAt(chart, event.offsetX, event.offsetY, motivationIds.length)
      if (index == null) return
      const motivationId = motivationIds[index]
      if (motivationId) onSelectMotivation(motivationId)
    }

    canvas.addEventListener('mousemove', handleMove)
    canvas.addEventListener('mouseleave', handleLeave)
    canvas.addEventListener('click', handleClick)
    return () => {
      canvas.removeEventListener('mousemove', handleMove)
      canvas.removeEventListener('mouseleave', handleLeave)
      canvas.removeEventListener('click', handleClick)
      handleLeave()
    }
  }, [onSelectMotivation, motivationIds])

  const chartData = useMemo(
    () => ({
      labels,
      // Drop sources whose data is missing for the current game (e.g. no
      // Game & Live Events profile) instead of rendering an empty/zeroed
      // series — showing a flat dataset under a real-looking label would
      // misrepresent which signals are actually available.
      datasets: MOTIVATION_SOURCE_ORDER.flatMap((source) => {
        const series = motivationScores[source]
        if (!series) return []
        return [
          {
            label: t(MOTIVATION_SOURCE_LABEL_KEYS[source]),
            data: series.map((s) => s.avgIntensity),
            backgroundColor: sourceColors[source].stroke,
            borderRadius: 3,
            borderSkipped: false as const,
            // The group of three bars takes ~78% of the row (leaving the
            // zebra-stripe gap between motivations), and inside the group each
            // bar fills ~70% of its slot so the three sources read as separate
            // bands with visible "air" between them rather than glued together.
            barPercentage: 0.7,
            categoryPercentage: 0.78,
            // Tooltip uses `usePointStyle` so swatches render as circles instead
            // of dataset-shaped rectangles.
            pointStyle: 'circle' as const,
            hidden: hiddenSources?.has(source) ?? false,
          },
        ]
      }),
    }),
    [labels, motivationScores, sourceColors, t, hiddenSources]
  )

  const options = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { left: 10 },
      },
      scales: {
        x: {
          min: 0,
          max: 5,
          ticks: { stepSize: 1, font: { size: 12 } },
          grid: { color: chartTheme.axis.grid, drawTicks: false },
          border: { display: false },
        },
        y: {
          ticks: { font: { size: 13 }, autoSkip: false, padding: 6 },
          grid: { display: false, drawTicks: false },
          border: { display: false },
          stacked: false,
        },
      },
      plugins: {
        // Per-chart legend disabled: the page renders a single shared
        // `MotivationSourceLegend` below both the radar and bar charts.
        legend: { display: false },
        tooltip: {
          // Show every visible dataset's value for the hovered motivation row
          // (axis 'y' because this is a horizontal bar chart) so the three
          // sources can be compared without hovering each bar individually.
          mode: 'index' as const,
          intersect: false,
          axis: 'y' as const,
          // Circular swatches + a calmer dark background so the navy
          // "Game & Live Events" colour stays legible (the default tooltip
          // pinned a tiny white-bordered square that read as empty).
          ...chartTheme.tooltip,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>) => ` ${ctx.dataset.label ?? ''}: ${ctx.parsed.x.toFixed(2)}`,
            labelColor: (ctx: TooltipItem<'bar'>) => {
              // Bar datasets store the solid source colour in `backgroundColor`
              // (no `borderColor` is set). Coerce to string – the dataset
              // option also allows functions/gradients but we never use those.
              const color = (typeof ctx.dataset.backgroundColor === 'string' ? ctx.dataset.backgroundColor : undefined) ?? chartTheme.swatch.fallback
              // Solid 1px white ring lifts the swatch off the dark tooltip
              // surface (the navy "Game & Live Events" dot otherwise
              // disappears into the background).
              return { backgroundColor: color, borderColor: chartTheme.swatch.border, borderWidth: 1 }
            },
          },
        },
      },
    }),
    [chartTheme]
  )

  const plugins = useMemo(() => [createZebraRowsPlugin(chartTheme.zebraFill), createRowHighlightPlugin(chartTheme.rowHighlightFill)], [chartTheme])

  // Chart-area height scales with the number of motivations so every group of
  // three bars gets enough vertical space to render at a comfortable
  // thickness. With a full set of motivations × 3 sources the previous fixed
  // 500px squeezed each bar to ~3-4px, which read as crammed.
  return (
    <Box sx={{ height: getMotivationChartHeight(labels.length) }}>
      <Bar ref={chartRef} data={chartData} options={options} plugins={plugins} />
    </Box>
  )
}
